import React from 'react';
import { ColumnSelect } from 'lib/components/ColumnSelect';
import { ERef, ModularSize } from 'lib/types';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { ColumnService } from 'lib/services/directory';
import { FilingTypeModel } from 'lib/model/objects/filingTypeModel';
import { getFirebaseContext } from 'utils/firebase';
import { getModelFromRef } from 'lib/model';
import { ModularSizeModel } from 'lib/model/objects/modularSizeModel';

interface ModularSizeSelectorProps {
  filingTypeModel: FilingTypeModel | null;
  selectedModularSize?: ERef<ModularSize>;
  onChange: (modularSize: ERef<ModularSize>) => void;
  canEditContent: boolean;
  orderId?: string;
}

function ModularSizeSelector({
  filingTypeModel,
  selectedModularSize,
  onChange,
  canEditContent,
  orderId
}: ModularSizeSelectorProps) {
  // Load available modular sizes from the filing type model
  const { value: availableModularSizes } = useAsyncEffect({
    fetchData: async () => {
      if (!filingTypeModel?.modelData.modularSizes) {
        return [];
      }

      const modularSizePromises = filingTypeModel.modelData.modularSizes.map(
        modularSizeRef =>
          getModelFromRef(
            ModularSizeModel,
            getFirebaseContext(),
            modularSizeRef
          )
      );
      return await Promise.all(modularSizePromises);
    },
    errorConfig: {
      service: ColumnService.ORDER_PLACEMENT,
      message: 'Failed to load modular sizes from filing type',
      tags: {
        orderId: orderId || 'unknown',
        filingTypeId: filingTypeModel?.id || 'unknown'
      }
    },
    dependencies: [filingTypeModel?.id]
  });

  // Don't render if no modular sizes are available
  if (!availableModularSizes || availableModularSizes.length === 0) {
    return null;
  }

  return (
    <ColumnSelect
      id="select-modular-size"
      labelText="Select a size"
      value={selectedModularSize?.id || ''}
      placeholder="Select a size"
      onChange={selectedId => {
        const selectedModularSize = availableModularSizes.find(
          size => size.id === selectedId
        );
        if (selectedModularSize) {
          onChange(selectedModularSize.ref);
        }
      }}
      disabled={!canEditContent}
      options={availableModularSizes.map(size => ({
        value: size.id,
        label: size.modelData.name
      }))}
      allowUndefined
    />
  );
}

export default ModularSizeSelector;
